import { useEffect, useState } from "react";

import { Link, useNavigate } from "react-router-dom";

import { PencilIcon, PlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import { createColumnHelper } from "@tanstack/react-table";

import { Button } from "../../components/base/Button";
import { Table } from "../../components/base/Table/Table";
import { ICustomer } from "../../models/Customer/Customer";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import getAddress from "../../utils/getAddress";
import { CustomerQueryKeys } from "../../data/Customer/keys";
import ConfirmModal from "../../components/shared/Confirm/ConfirmModal";
import DomainHealthIndicator from "../../components/shared/DomainHealthIndicator";
import useCustomerMutations from "../../data/Customer/useCustomerMutations";

const colHelper = createColumnHelper<ICustomer>();

export function Customers() {
  const { remove } = useCustomerMutations();
  const { setInfo } = usePageNameContext();
  const navigate = useNavigate();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteId, setDeleteId] = useState("");

  useEffect(() => {
    setInfo({
      name: "Customers",
      desc: "View and create customers.",
      pageTitle: "Customers",
    });
  }, []);

  const cols = [
    colHelper.accessor("id", { header: "Customer ID" }),
    colHelper.accessor("name", { header: "Customer Name" }),
    colHelper.display({
      header: "Domain",
      cell: (info) => {
        const domain = info.row.original.domain;
        return (
          <div className="flex items-center space-x-2">
            <DomainHealthIndicator domain={domain} />
            <span>{domain}</span>
          </div>
        );
      },
    }),
    colHelper.display({
      header: "Address",
      cell: (info) => {
        let row = info.row.original;
        return getAddress({
          line1: row.line1,
          line2: row.line2,
          line3: row.line3,
          line4: row.line4,
          locality: row.locality,
          town: row.town,
          county: row.county,
          postcode: row.postcode,
        });
      },
    }),
    colHelper.display({
      header: "Actions",
      cell: (info) => (
        <div className="flex items-center space-x-4">
          <PencilIcon
            className="w-5 h-5 opacity-70 hover:opacity-100 hover:cursor-pointer"
            onClick={() => navigate(`/customer/${info.row.original.id}`)}
          />
          <TrashIcon
            className="w-5 h-5 opacity-70 hover:opacity-100 hover:cursor-pointer"
            onClick={() => { setDeleteId(info.row.original.id); setDeleteOpen(true); }}
          />
        </div>
      ),
    }),
  ];

  const handleDelete = () => {

    remove.mutate(deleteId, {
      onSuccess: () => {
        setDeleteOpen(false);
        setDeleteId("");
      },
      onError: (err) => {
        console.error(err);
      },
    });
  }

  return (
    <>
      <ConfirmModal open={deleteOpen} setOpen={setDeleteOpen}
        title={"Delete Customer?"} message={`Are you sure you want to delete this customer?\n It's a soft delete anyway, so don't worry too much.`}
        confirmButtonText={"Delete"}
        onConfirm={handleDelete}
        isLoading={false}
      />
      <div className="space-y-6">
        <div className="flex justify-between">
          <Link to="new">
            <Button className="flex items-center space-x-2">
              <PlusIcon className="h-5 w-5 -ml-1" />
              <p>Create Customer</p>
            </Button>
          </Link>
        </div>
        <Table
          url="/customer"
          queryKey={CustomerQueryKeys.all()}
          columns={cols}
        />
      </div>
    </>
  );
}
